import { z } from "zod";

export const MAX_GALLERY_PROMOTIONS = 30;
export const MAX_GALLERY_UPLOAD_BYTES = 4 * 1024 * 1024;
export const GALLERY_UPLOAD_TYPES: Record<string, string> = {
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/webp": "webp"
};

export interface GalleryPromotion {
    id: string;
    imageUrl: string;
    alt: string;
    position: number;
    enabled: boolean;
    filters?: GalleryPromotionFilters | undefined;
}

const galleryPromotionFiltersSchema = z.object({
    match: z.enum(["all", "any"]),
    minPrice: z.number().finite().nonnegative().optional(),
    maxPrice: z.number().finite().nonnegative().optional(),
    make: z.string().trim().max(100).optional(),
    model: z.string().trim().max(100).optional()
}).strict().refine((filters) => filters.minPrice === undefined || filters.maxPrice === undefined || filters.minPrice <= filters.maxPrice, {
    message: "Minimum price must not exceed maximum price", path: ["maxPrice"]
});

export type GalleryPromotionFilters = z.infer<typeof galleryPromotionFiltersSchema>;

export interface GalleryVehicle {
    price?: number | null | undefined;
    make?: string | null | undefined;
    model?: string | null | undefined;
}

/** Blank filters apply to every vehicle. Missing inventory values never satisfy a condition. */
export function matchesGalleryPromotion(filters: GalleryPromotionFilters | undefined, vehicle: GalleryVehicle = {}): boolean {
    if (!filters) return true;
    const conditions: boolean[] = [];
    if (filters.minPrice !== undefined || filters.maxPrice !== undefined) {
        conditions.push(typeof vehicle.price === "number" && Number.isFinite(vehicle.price) && vehicle.price > 0 &&
            (filters.minPrice === undefined || vehicle.price >= filters.minPrice) &&
            (filters.maxPrice === undefined || vehicle.price <= filters.maxPrice));
    }
    for (const field of ["make", "model"] as const) {
        const expected = filters[field]?.trim().toLowerCase();
        if (expected) conditions.push(vehicle[field]?.trim().toLowerCase() === expected);
    }
    return !conditions.length || (filters.match === "any" ? conditions.some(Boolean) : conditions.every(Boolean));
}

export interface GalleryPromotionSettings {
    items: GalleryPromotion[];
    revision: number;
}

export const DEFAULT_GALLERY_PROMOTIONS: GalleryPromotion[] = [
    { id: "cars-under-20k", imageUrl: "/media/gallery-promotions/cars-under-20k.png", alt: "Over 100 vehicles under $20,000. Call us about our other used cars in stock.", position: 4, enabled: true },
    { id: "tc-difference", imageUrl: "/media/gallery-promotions/tc-difference.png", alt: "The THIScar difference: no auction cars, no documentary or junk fees, no high-pressure sales; upfront pricing. This retired graphic contains an outdated return-policy claim.", position: 7, enabled: false },
    { id: "no-dealer-fees", imageUrl: "/media/gallery-promotions/THIScar-No-Dealer-Fees-Photo-Feed.png", alt: "No dealer fees. No doc fee, no prep fee, and no surprises at the finish line.", position: 10, enabled: true },
    { id: "tc-pressure", imageUrl: "/media/gallery-promotions/tc-pressure.png", alt: "The only pressure around here is in the tires. Free ice cream, free toy cars for the kids, and a movie theater waiting room. Bring the whole family.", position: 13, enabled: true }
];

const defaultUrls = new Set(DEFAULT_GALLERY_PROMOTIONS.map((item) => item.imageUrl));

export function isGalleryPromotionUrl(value: string): boolean {
    if (defaultUrls.has(value)) return true;
    try {
        const url = new URL(value);
        return url.protocol === "https:" && url.hostname === "storage.googleapis.com" &&
            !url.port && !url.username && !url.password && !url.search && !url.hash &&
            /^\/[^/]+\/gallery-promotions\/[a-z0-9-]+\.(jpg|png|webp)$/.test(decodeURIComponent(url.pathname));
    } catch {
        return false;
    }
}

export const galleryPromotionsSchema = z.array(z.object({
    id: z.string().regex(/^[a-z0-9-]{1,80}$/, "Invalid image ID"),
    imageUrl: z.string().refine(isGalleryPromotionUrl, "Upload an image before saving"),
    alt: z.string().trim().min(1, "Add an image description").max(500, "Descriptions must be 500 characters or fewer"),
    position: z.number().int("Positions must be whole numbers").min(1, "Positions start at 1").max(1000, "Positions cannot exceed 1,000"),
    enabled: z.boolean(),
    filters: galleryPromotionFiltersSchema.optional()
})).max(MAX_GALLERY_PROMOTIONS).superRefine((items, context) => {
    const ids = new Set<string>();
    const positions = new Set<number>();
    items.forEach((item, index) => {
        if (ids.has(item.id)) context.addIssue({ code: "custom", path: [index, "id"], message: "Each image must have a unique ID" });
        if (item.enabled && positions.has(item.position)) {
            context.addIssue({ code: "custom", path: [index, "position"], message: `Position ${item.position} is used by more than one enabled image` });
        }
        ids.add(item.id);
        if (item.enabled) positions.add(item.position);
    });
});

export interface VehicleGallerySlide {
    id: string;
    src: string;
    alt: string;
    kind: "vehicle" | "promotion" | "placeholder";
}

/** Positions are one-based slots in the finished gallery. Short galleries append overflow in order. */
export function buildVehicleGallery(
    images: readonly string[],
    promotions: readonly GalleryPromotion[],
    vehicleName: string,
    vehicle?: GalleryVehicle
): VehicleGallerySlide[] {
    const slides: VehicleGallerySlide[] = images.filter(Boolean).map((src, index) => ({
        id: `vehicle-${index}`, src, alt: `${vehicleName} photo ${index + 1}`, kind: "vehicle"
    }));
    // Never replace a vehicle with only advertising when its photography is unavailable.
    if (!slides.length) return [{ id: "placeholder", src: "/media/coming-soon.png", alt: `${vehicleName} photos coming soon`, kind: "placeholder" }];

    const active = promotions.filter((item) => item.enabled && item.imageUrl !== "/media/gallery-promotions/tc-difference.png" && matchesGalleryPromotion(item.filters, vehicle)).sort((a, b) => a.position - b.position || a.id.localeCompare(b.id));
    for (const item of active) {
        slides.splice(Math.min(item.position - 1, slides.length), 0, {
            id: `promotion-${item.id}`, src: item.imageUrl, alt: item.alt, kind: "promotion"
        });
    }
    return slides;
}
